import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Star,
  ChevronRight,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

const PublicSurvey = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [thankYouData, setThankYouData] = useState(null);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [respondentInfo, setRespondentInfo] = useState({ name: '', email: '' });
  const [startTime] = useState(new Date().toISOString());
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_URL}/public/survey/${surveyId}`);
      setSurvey(response.data);
    } catch (error) {
      console.error('Error fetching survey:', error);
      if (error.response?.status === 404) {
        setError('Survey not found');
      } else if (error.response?.status === 403) {
        setError('This survey is no longer accepting responses');
      } else {
        setError('Failed to load survey');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleCheckboxChange = (questionId, option, checked) => {
    const currentAnswers = answers[questionId] || [];
    if (checked) {
      handleAnswer(questionId, [...currentAnswers, option]);
    } else {
      handleAnswer(questionId, currentAnswers.filter(a => a !== option));
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const response = await api.post(`${API_URL}/public/survey/${surveyId}/submit`, {
        answers,
        respondent_name: respondentInfo.name || null,
        respondent_email: respondentInfo.email || null,
        started_at: startTime
      });
      
      setThankYouData(response.data);
      setSubmitted(true);
      
      // Redirect if configured
      if (response.data.redirect_url) {
        setTimeout(() => {
          window.location.href = response.data.redirect_url;
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('Failed to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isQuestionAnswered = (question) => {
    const answer = answers[question.id];
    if (!question.required) return true;
    if (Array.isArray(answer)) return answer.length > 0;
    return answer !== undefined && answer !== null && answer !== '';
  };

  const canProceed = () => {
    if (showIntro) {
      if (survey?.settings?.collect_name && !respondentInfo.name) return false;
      if (survey?.settings?.collect_email && !respondentInfo.email) return false;
      return true;
    }
    const question = survey?.questions?.[currentQuestion];
    return question ? isQuestionAnswered(question) : true;
  };

  const goNext = () => {
    if (currentQuestion < survey.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const goPrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const branding = survey?.branding || {};
  const primaryColor = branding.primary_color || '#F97316';
  const bgColor = branding.background_color || '#FFFFFF';
  const textColor = branding.text_color || '#1F2937';
  const buttonColor = branding.button_color || '#F97316';
  const buttonTextColor = branding.button_text_color || '#FFFFFF';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: textColor }}>{error}</h1>
          <p className="text-gray-500">
            {error === 'This survey is no longer accepting responses' 
              ? 'The survey owner has closed this survey.'
              : 'Please check the link and try again.'}
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <div className="text-center max-w-md">
          {branding.logo_url && (
            <img src={branding.logo_url} alt="Logo" className="h-12 mx-auto mb-6" />
          )}
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <CheckCircle2 className="h-10 w-10" style={{ color: primaryColor }} />
          </div>
          <h1 className="text-3xl font-bold mb-4" style={{ color: textColor }}>
            {thankYouData?.thank_you_title || survey?.thank_you_title || 'Thank You!'}
          </h1>
          <p className="text-gray-600 text-lg">
            {thankYouData?.thank_you_message || survey?.thank_you_message || 'Your response has been recorded.'}
          </p>
          {thankYouData?.redirect_url && (
            <p className="text-sm text-gray-400 mt-4">
              Redirecting you shortly...
            </p>
          )}
        </div>
      </div>
    );
  }

  const progress = showIntro ? 0 : ((currentQuestion + 1) / survey.questions.length) * 100;
  const totalQuestions = survey?.questions?.length || 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      {/* Progress Bar */}
      {survey?.settings?.show_progress_bar && !showIntro && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
          <div 
            className="h-full transition-all duration-300"
            style={{ width: `${progress}%`, backgroundColor: primaryColor }}
          />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-8 md:py-16">
        {/* Logo */}
        {branding.logo_url && (
          <div className="mb-8 text-center">
            <img src={branding.logo_url} alt="Logo" className="h-10 mx-auto" />
          </div>
        )}

        {/* Intro Page */}
        {showIntro ? (
          <div className="space-y-8" data-testid="survey-intro">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>
                {survey.title}
              </h1>
              {survey.description && (
                <p className="text-lg text-gray-600">{survey.description}</p>
              )}
            </div>

            {/* Collect respondent info */}
            {(survey.settings?.collect_name || survey.settings?.collect_email) && (
              <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
                <p className="text-sm text-gray-500">Before we begin:</p>
                
                {survey.settings?.collect_name && (
                  <div>
                    <Label style={{ color: textColor }}>Your Name</Label>
                    <Input
                      value={respondentInfo.name}
                      onChange={(e) => setRespondentInfo({ ...respondentInfo, name: e.target.value })}
                      placeholder="Enter your name"
                      className="mt-1"
                      data-testid="respondent-name-input"
                    />
                  </div>
                )}
                
                {survey.settings?.collect_email && (
                  <div>
                    <Label style={{ color: textColor }}>Your Email</Label>
                    <Input
                      type="email"
                      value={respondentInfo.email}
                      onChange={(e) => setRespondentInfo({ ...respondentInfo, email: e.target.value })}
                      placeholder="Enter your email"
                      className="mt-1"
                      data-testid="respondent-email-input"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="text-center">
              <Button
                size="lg"
                onClick={() => setShowIntro(false)}
                disabled={!canProceed()}
                className="px-8 py-6 text-lg rounded-full"
                style={{ 
                  backgroundColor: buttonColor, 
                  color: buttonTextColor,
                  opacity: canProceed() ? 1 : 0.5
                }}
                data-testid="start-survey-btn"
              >
                Start Survey
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-gray-400 mt-4">
                {totalQuestions} question{totalQuestions !== 1 ? 's' : ''} • Takes about {Math.ceil(totalQuestions * 0.5)} minutes
              </p>
            </div>
          </div>
        ) : (
          /* Question View */
          <div className="space-y-8" data-testid="survey-questions">
            {/* Question Counter */}
            <div className="text-center">
              <span className="text-sm font-medium px-4 py-2 rounded-full" style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                Question {currentQuestion + 1} of {totalQuestions}
              </span>
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 min-h-[300px] flex flex-col">
              {survey.questions.map((question, idx) => {
                if (idx !== currentQuestion) return null;

                return (
                  <div key={question.id} className="flex-1 space-y-6">
                    <div>
                      <h2 className="text-xl md:text-2xl font-semibold mb-2" style={{ color: textColor }}>
                        {question.question}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                      </h2>
                      {question.description && (
                        <p className="text-gray-500">{question.description}</p>
                      )}
                    </div>

                    {/* Text Input */}
                    {question.type === 'text' && (
                      <Input
                        value={answers[question.id] || ''}
                        onChange={(e) => handleAnswer(question.id, e.target.value)}
                        placeholder="Your answer..."
                        className="text-lg py-6"
                        data-testid={`question-${question.id}-input`}
                      />
                    )}

                    {/* Textarea */}
                    {question.type === 'textarea' && (
                      <Textarea
                        value={answers[question.id] || ''}
                        onChange={(e) => handleAnswer(question.id, e.target.value)}
                        placeholder="Your answer..."
                        rows={5}
                        className="text-lg"
                        data-testid={`question-${question.id}-textarea`}
                      />
                    )}

                    {/* Multiple Choice */}
                    {question.type === 'multiple_choice' && (
                      <RadioGroup
                        value={answers[question.id] || ''}
                        onValueChange={(value) => handleAnswer(question.id, value)}
                        className="space-y-3"
                      >
                        {question.options.map((option, optIdx) => (
                          <label
                            key={optIdx}
                            className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              answers[question.id] === option 
                                ? 'border-current bg-opacity-10' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={answers[question.id] === option ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                          >
                            <RadioGroupItem value={option} className="mr-3" />
                            <span className="text-lg">{option}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    )}

                    {/* Checkbox */}
                    {question.type === 'checkbox' && (
                      <div className="space-y-3">
                        {question.options.map((option, optIdx) => {
                          const isChecked = (answers[question.id] || []).includes(option);
                          return (
                            <label
                              key={optIdx}
                              className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                isChecked 
                                  ? 'border-current bg-opacity-10' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={isChecked ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => handleCheckboxChange(question.id, option, checked)}
                                className="mr-3"
                              />
                              <span className="text-lg">{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* Dropdown */}
                    {question.type === 'dropdown' && (
                      <Select
                        value={answers[question.id] || ''}
                        onValueChange={(value) => handleAnswer(question.id, value)}
                      >
                        <SelectTrigger className="text-lg py-6">
                          <SelectValue placeholder="Select an option..." />
                        </SelectTrigger>
                        <SelectContent>
                          {question.options.map((option, optIdx) => (
                            <SelectItem key={optIdx} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Rating */}
                    {question.type === 'rating' && (
                      <div className="flex flex-col items-center space-y-4">
                        <div className="flex gap-2">
                          {[...Array(question.max_rating || 5)].map((_, i) => {
                            const value = i + 1;
                            const isSelected = answers[question.id] >= value;
                            return (
                              <button
                                key={i}
                                onClick={() => handleAnswer(question.id, value)}
                                className="p-2 transition-transform hover:scale-110"
                              >
                                <Star 
                                  className={`w-10 h-10 ${isSelected ? 'fill-current' : ''}`}
                                  style={{ color: isSelected ? '#FCD34D' : '#D1D5DB' }}
                                />
                              </button>
                            );
                          })}
                        </div>
                        {question.rating_labels && (
                          <div className="flex justify-between w-full text-sm text-gray-500">
                            <span>{question.rating_labels['1'] || 'Poor'}</span>
                            <span>{question.rating_labels[String(question.max_rating)] || 'Excellent'}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* NPS */}
                    {question.type === 'nps' && (
                      <div className="space-y-4">
                        <div className="flex flex-wrap justify-center gap-2">
                          {[...Array(11)].map((_, i) => {
                            const isSelected = answers[question.id] === i;
                            let bgClass = 'bg-gray-100 hover:bg-gray-200';
                            if (isSelected) {
                              if (i <= 6) bgClass = 'bg-red-500 text-white';
                              else if (i <= 8) bgClass = 'bg-yellow-500 text-white';
                              else bgClass = 'bg-green-500 text-white';
                            }
                            return (
                              <button
                                key={i}
                                onClick={() => handleAnswer(question.id, i)}
                                className={`w-12 h-12 rounded-xl font-semibold transition-all ${bgClass}`}
                              >
                                {i}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>{question.rating_labels?.['0'] || 'Not at all likely'}</span>
                          <span>{question.rating_labels?.['10'] || 'Extremely likely'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={goPrev}
                disabled={currentQuestion === 0}
                className="px-6"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {currentQuestion === totalQuestions - 1 ? (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  className="px-8 py-6 text-lg rounded-full"
                  style={{ 
                    backgroundColor: buttonColor, 
                    color: buttonTextColor,
                    opacity: canProceed() ? 1 : 0.5
                  }}
                  data-testid="submit-survey-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Survey'
                  )}
                </Button>
              ) : (
                <Button
                  onClick={goNext}
                  disabled={!canProceed()}
                  className="px-8 py-6 text-lg rounded-full"
                  style={{ 
                    backgroundColor: buttonColor, 
                    color: buttonTextColor,
                    opacity: canProceed() ? 1 : 0.5
                  }}
                  data-testid="next-question-btn"
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-xs text-gray-400">
            Powered by SalesQualifyd
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicSurvey;
